import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";

const RELIGIONS = ["Hindu", "Muslim", "Christian", "Sikh", "Jain", "Buddhist", "Parsi", "Jewish", "Other"];
const EDUCATIONS = ["Undergraduate", "Graduate", "Postgraduate", "PhD / Doctorate", "Professional"];
const MARITAL = ["Never Married", "Divorced", "Widowed", "Awaiting Divorce"];
const DIET = ["Veg", "Non-Veg", "Eggetarian", "Vegan", "Jain"];

const EMPTY_FILTERS = {
  q: "",
  gender: "",
  age_min: "",
  age_max: "",
  religion: "",
  caste: "",
  city: "",
  education: "",
  marital_status: "",
  diet: "",
};

export default function Search() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [results, setResults] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user || typeof user !== "object") {
      navigate("/login", { replace: true, state: { from: "/search" } });
    }
  }, [user, loading, navigate]);

  const runSearch = useCallback(
    async (opts = {}) => {
      setBusy(true);
      setError("");
      try {
        const params = { ...filters, page, page_size: 12, ...opts };
        // strip empty strings so backend defaults apply
        const clean = Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== "" && v !== null && v !== undefined),
        );
        const { data } = await api.get("/search/profiles", { params: clean });
        setResults(data.items);
        setTotal(data.total);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setBusy(false);
      }
    },
    [filters, page],
  );

  useEffect(() => {
    if (user && typeof user === "object") runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, user]);

  const applyFilters = () => {
    setPage(1);
    runSearch({ page: 1 });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setTimeout(() => runSearch({ ...EMPTY_FILTERS, page: 1 }), 0);
  };

  const update = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));

  if (loading || !user || typeof user !== "object") {
    return (
      <div>
        <Navbar />
        <div className="container py-5">
          <div className="sb-card">Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="container py-4" data-testid="search-page">
        <div className="d-flex justify-content-between align-items-end flex-wrap gap-2 mb-4">
          <div>
            <div className="sb-kicker mb-1">Search &amp; Match</div>
            <h1 className="h2 mb-1">Find a match.</h1>
            <p className="text-muted mb-0">
              {total > 0 ? `${total} profile${total === 1 ? "" : "s"} match your filters.` : "Try widening your filters."}
            </p>
          </div>
        </div>

        <div className="row g-4">
          <aside className="col-lg-3">
            <div className="sb-card" data-testid="filters">
              <h2 className="h6 mb-3">Filters</h2>

              <div className="mb-3">
                <label className="form-label">Keyword</label>
                <input
                  className="form-control form-control-sm"
                  value={filters.q}
                  onChange={(e) => update("q", e.target.value)}
                  placeholder="Name, city, occupation…"
                  data-testid="filter-q"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Gender</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.gender}
                  onChange={(e) => update("gender", e.target.value)}
                  data-testid="filter-gender"
                >
                  <option value="">Any</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Age</label>
                <div className="d-flex gap-2">
                  <input
                    type="number"
                    min="18"
                    max="99"
                    className="form-control form-control-sm"
                    placeholder="Min"
                    value={filters.age_min}
                    onChange={(e) => update("age_min", e.target.value)}
                    data-testid="filter-age-min"
                  />
                  <input
                    type="number"
                    min="18"
                    max="99"
                    className="form-control form-control-sm"
                    placeholder="Max"
                    value={filters.age_max}
                    onChange={(e) => update("age_max", e.target.value)}
                    data-testid="filter-age-max"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Religion</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.religion}
                  onChange={(e) => update("religion", e.target.value)}
                  data-testid="filter-religion"
                >
                  <option value="">Any</option>
                  {RELIGIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Caste</label>
                <input
                  className="form-control form-control-sm"
                  value={filters.caste}
                  onChange={(e) => update("caste", e.target.value)}
                  data-testid="filter-caste"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">City</label>
                <input
                  className="form-control form-control-sm"
                  value={filters.city}
                  onChange={(e) => update("city", e.target.value)}
                  data-testid="filter-city"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Education</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.education}
                  onChange={(e) => update("education", e.target.value)}
                  data-testid="filter-education"
                >
                  <option value="">Any</option>
                  {EDUCATIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Marital status</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.marital_status}
                  onChange={(e) => update("marital_status", e.target.value)}
                  data-testid="filter-marital"
                >
                  <option value="">Any</option>
                  {MARITAL.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Diet</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.diet}
                  onChange={(e) => update("diet", e.target.value)}
                  data-testid="filter-diet"
                >
                  <option value="">Any</option>
                  {DIET.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div className="d-flex gap-2">
                <button className="btn btn-primary btn-sm flex-fill" onClick={applyFilters} data-testid="filter-apply">
                  Apply
                </button>
                <button className="btn btn-outline-primary btn-sm" onClick={resetFilters} data-testid="filter-reset">
                  Reset
                </button>
              </div>
            </div>
          </aside>

          <section className="col-lg-9">
            {error && <div className="alert alert-danger" data-testid="search-error">{error}</div>}

            {results === null ? (
              <div className="sb-card">Searching…</div>
            ) : results.length === 0 ? (
              <div className="sb-card text-center py-5" data-testid="empty-results">
                <div className="sb-kicker mb-2">No matches</div>
                <h3 className="h5">Try relaxing a filter or two.</h3>
              </div>
            ) : (
              <>
                <div className="row g-3" data-testid="search-results">
                  {results.map((r) => (
                    <ProfileCard key={r.id} r={r} />
                  ))}
                </div>

                <div className="d-flex justify-content-between align-items-center mt-4">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    disabled={page <= 1 || busy}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="page-prev"
                  >
                    ← Previous
                  </button>
                  <span className="text-muted small">Page {page}</span>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    disabled={results.length < 12 || busy}
                    onClick={() => setPage((p) => p + 1)}
                    data-testid="page-next"
                  >
                    Next →
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function ProfileCard({ r }) {
  return (
    <div className="col-md-6 col-xl-4">
      <div className="sb-profile-card h-100" data-testid={`profile-card-${r.id}`}>
        <div className="sb-profile-photo">
          {r.photo_url ? (
            <img src={r.photo_url} alt={r.full_name} />
          ) : (
            <div className="sb-profile-photo-empty">
              <span>{(r.full_name || "?").charAt(0)}</span>
            </div>
          )}
          {r.is_premium && (
            <span className="sb-badge-soft sb-badge-gold sb-profile-premium">Premium</span>
          )}
        </div>
        <div className="p-3">
          <h3 className="h5 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {r.full_name}
          </h3>
          <div className="small text-muted">
            {[r.age && `${r.age} yrs`, r.height, r.gender && r.gender[0].toUpperCase() + r.gender.slice(1)]
              .filter(Boolean)
              .join(" · ")}
          </div>
          <div className="small mt-2" style={{ color: "var(--sb-body)" }}>
            {[r.education, r.occupation].filter(Boolean).join(" · ") || "—"}
          </div>
          <div className="small text-muted">
            {[r.religion, r.caste, r.city].filter(Boolean).join(" · ") || "—"}
          </div>
          <Link
            to={`/profile/${r.id}`}
            className="btn btn-outline-primary btn-sm w-100 mt-3"
            data-testid={`view-profile-${r.id}`}
          >
            View profile
          </Link>
        </div>
      </div>
    </div>
  );
}
